import {
  mdiBookOpenPageVariant,
  mdiBullhorn,
  mdiNewspaperVariantOutline,
  mdiCreditCardClockOutline,
  mdiCompassOutline,
  mdiChartBar,
  mdiClockAlertOutline,
  mdiCashRegister,
  mdiScaleBalance,
  mdiChartDonut,
  mdiClipboardCheckOutline,
} from '@mdi/js';

/**
 * Widget Registry — central manifest of all available dashboard widgets.
 * Each widget is lazy-loaded and self-contained.
 *
 * released: true  — visible to users (Widget Library + default layout)
 * released: false — code ships but widget is hidden until its release PR flips this flag
 */
export const widgetRegistry = {
  // ── Phase 1 ─────────────────────────────────────────────────────────────
  'verse-of-the-day': {
    id: 'verse-of-the-day',
    name: 'Verse of the Day',
    description: 'Daily Bible verse with commentary',
    category: 'Personal',
    icon: mdiBookOpenPageVariant,
    defaultSize: 'sm',
    defaultEnabled: true,
    released: true,
    component: () => import('./widgets/VerseOfTheDayWidget.vue'),
  },
  announcements: {
    id: 'announcements',
    name: 'Announcements',
    description: 'Recent system announcements and notices',
    category: 'System',
    icon: mdiBullhorn,
    defaultSize: 'sm',
    defaultEnabled: true,
    released: true,
    component: () => import('./widgets/AnnouncementsWidget.vue'),
  },
  'quick-nav': {
    id: 'quick-nav',
    name: 'Quick Navigation',
    description: 'Shortcut buttons to frequently used screens',
    category: 'Navigation',
    icon: mdiCompassOutline,
    defaultSize: 'sm',
    defaultEnabled: true,
    released: true,
    component: () => import('./widgets/QuickNavWidget.vue'),
  },

  // ── Pending Release (flip released: true in follow-up PRs) ──────────────
  'unposted-payments': {
    id: 'unposted-payments',
    name: 'Unposted Payments',
    description: 'Count of payments waiting to be posted',
    category: 'Tax',
    icon: mdiCreditCardClockOutline,
    defaultSize: 'sm',
    defaultEnabled: true,
    released: true,
    component: () => import('./widgets/UnpostedPaymentsWidget.vue'),
  },
  'whats-new': {
    id: 'whats-new',
    name: "What's New",
    description: 'Recent feature updates and upcoming improvements',
    category: 'System',
    icon: mdiNewspaperVariantOutline,
    defaultSize: 'lg',
    defaultEnabled: true,
    released: true,
    component: () => import('./widgets/WhatsNewWidget.vue'),
  },
  'transaction-volume': {
    id: 'transaction-volume',
    name: 'Transaction Volume',
    description: 'Daily payment activity chart with prior year comparison',
    category: 'Tax',
    icon: mdiChartBar,
    defaultSize: 'lg',
    defaultEnabled: true,
    released: true,
    component: () => import('./widgets/TransactionVolumeWidget.vue'),
  },
  'web-payment-queue': {
    id: 'web-payment-queue',
    name: 'Web Payment Queue',
    description: 'Pending Payrix/web payment batches awaiting import',
    category: 'Tax',
    icon: mdiClockAlertOutline,
    defaultSize: 'sm',
    defaultEnabled: false,
    released: true,
    component: () => import('./widgets/WebPaymentQueueWidget.vue'),
  },
  'cash-drawer': {
    id: 'cash-drawer',
    name: 'Cash Drawer',
    description: "Today's collections broken down by payment type",
    category: 'Tax',
    icon: mdiCashRegister,
    defaultSize: 'md',
    defaultEnabled: false,
    released: false,
    component: () => import('./widgets/CashDrawerWidget.vue'),
  },
  'gl-balance-status': {
    id: 'gl-balance-status',
    name: 'GL Balance Status',
    description: 'Fund reconciliation balance check for current fiscal year',
    category: 'GL',
    icon: mdiScaleBalance,
    defaultSize: 'md',
    defaultEnabled: false,
    released: true,
    component: () => import('./widgets/GlBalanceStatusWidget.vue'),
  },
  'collections-by-category': {
    id: 'collections-by-category',
    name: 'Collections by Category',
    description: 'Month-to-date collections donut chart by tax category',
    category: 'Tax',
    icon: mdiChartDonut,
    defaultSize: 'lg',
    defaultEnabled: false,
    released: false,
    component: () => import('./widgets/CollectionsByCategoryWidget.vue'),
  },
  'eom-readiness': {
    id: 'eom-readiness',
    name: 'EOM Readiness',
    description: 'End-of-month checklist — 6 steps tracked in real time',
    category: 'System',
    icon: mdiClipboardCheckOutline,
    defaultSize: 'md',
    defaultEnabled: false,
    released: true,
    component: () => import('./widgets/EomReadinessWidget.vue'),
  },
};

/** Size to Vuetify cols mapping */
export const sizeToCols = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
};

/** Get default widget layout for new users */
export function getDefaultLayout() {
  const defaultOrder = [
    'verse-of-the-day',
    'announcements',
    'quick-nav',
    'unposted-payments',
    'whats-new',
    'transaction-volume',
    'web-payment-queue',
    'cash-drawer',
    'gl-balance-status',
    'collections-by-category',
    'eom-readiness',
  ];
  return defaultOrder
    .filter((id) => {
      const w = widgetRegistry[id];
      return w && w.released && w.defaultEnabled;
    })
    .map((id, index) => ({
      id,
      enabled: true,
      order: index,
      size: widgetRegistry[id].defaultSize,
    }));
}

export default widgetRegistry;
